/**
 * Fund play GBUX — player-facing (no agent inventory / Cash App marketing).
 * 1) Transfer on-chain GBUX SPL → play (1:1) — preferred
 * 2) SOL / USDT purchase → play @ $0.001 (5% fee)
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useWalletState } from "@/contexts/wallet-context";
import { isLoggedIn, loginWithGrudgeId } from "@/lib/grudge-auth";
import { GBUX, FLEET } from "@shared/fleet";
import { BRAND } from "@shared/brand";
import {
  transferAndCreditPlay,
  creditPlayFromTx,
  purchaseSolAndCreditPlay,
  purchaseUsdtAndCreditPlay,
  PLAY_DEPOSIT_SSOT,
} from "@/lib/gbux-transfer";
import { formatPlayGbux } from "@shared/gbux-math";
import { Loader2, Coins, ShieldCheck } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

interface BuyGbuxModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BuyGbuxModal({ open, onOpenChange }: BuyGbuxModalProps) {
  const { toast } = useToast();
  const { wallet, refreshBalances, getConnectedWallet } = useWalletState();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"gbux" | "sol" | "usdt">("gbux");
  const [gbuxAmount, setGbuxAmount] = useState("100");
  const [solAmount, setSolAmount] = useState("0.05");
  const [usdtAmount, setUsdtAmount] = useState("10");
  const [txSig, setTxSig] = useState("");
  const [loading, setLoading] = useState(false);
  const loggedIn = isLoggedIn();
  const agentAddr = PLAY_DEPOSIT_SSOT.agent;

  const afterCredit = async (data: {
    gbuxCredited?: number;
    playGbux?: number;
  }) => {
    toast({
      title: "Play GBUX credited",
      description: `+${formatPlayGbux(data.gbuxCredited ?? 0)} real → play ${formatPlayGbux(data.playGbux ?? 0)}`,
    });
    setTxSig("");
    await refreshBalances();
    void qc.invalidateQueries({ queryKey: ["wallet-scopes"] });
    void qc.invalidateQueries({ queryKey: ["poker-profile"] });
    onOpenChange(false);
  };

  const onSendFromWallet = async () => {
    const amt = Math.floor(parseFloat(gbuxAmount) || 0);
    if (amt < 1) {
      toast({ title: "Enter GBUX ≥ 1", variant: "destructive" });
      return;
    }
    const connected = getConnectedWallet();
    if (!connected) {
      toast({
        title: "Connect Phantom/Solflare first",
        description: "Required to sign the on-chain GBUX transfer",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      toast({
        title: "Approve in wallet",
        description: `Sign ${amt} GBUX transfer to fund play`,
      });
      const result = await transferAndCreditPlay({
        wallet: connected,
        amountHuman: amt,
        depositTo: agentAddr,
      });
      await afterCredit({
        gbuxCredited: result.gbuxCredited || result.amount,
        playGbux: result.playGbux,
      });
    } catch (e) {
      toast({
        title: "Transfer failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /** SOL/USDT purchase: payment stays on BUDBai agent; play GBUX credited on edge */
  const onBuyWithSol = async () => {
    const amt = parseFloat(solAmount) || 0;
    if (amt <= 0) {
      toast({ title: "Enter SOL amount", variant: "destructive" });
      return;
    }
    const connected = getConnectedWallet();
    if (!connected) {
      toast({
        title: "Connect Phantom / wallet first",
        description: "Required to sign the SOL transfer to BUDBai",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      toast({
        title: "Approve in wallet",
        description: `Send ${amt} SOL → agent · play GBUX credited after confirm`,
      });
      const result = await purchaseSolAndCreditPlay({
        wallet: connected,
        amountSol: amt,
        depositTo: agentAddr,
      });
      await afterCredit({
        gbuxCredited: result.gbuxCredited,
        playGbux: result.playGbux,
      });
    } catch (e) {
      toast({
        title: "SOL purchase failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onBuyWithUsdt = async () => {
    const amt = parseFloat(usdtAmount) || 0;
    if (amt < 0.01) {
      toast({ title: "Enter USDT amount ≥ 0.01", variant: "destructive" });
      return;
    }
    const connected = getConnectedWallet();
    if (!connected) {
      toast({
        title: "Connect wallet first",
        description: "Required to sign USDT SPL transfer",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      toast({
        title: "Approve in wallet",
        description: `Send ${amt} USDT → agent · play GBUX after verify`,
      });
      const result = await purchaseUsdtAndCreditPlay({
        wallet: connected,
        amountUsdt: amt,
        depositTo: agentAddr,
      });
      await afterCredit({
        gbuxCredited: result.gbuxCredited,
        playGbux: result.playGbux,
      });
    } catch (e) {
      toast({
        title: "USDT purchase failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (!wallet.publicKey) {
      toast({
        title: "Connect wallet",
        description: "Connect a wallet address first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      if (!txSig || txSig.length < 40) {
        toast({
          title: "Paste Solana tx signature",
          description: "Or use Sign & send above — paste only if you already sent",
          variant: "destructive",
        });
        return;
      }

      if (tab === "gbux") {
        const credit = await creditPlayFromTx(wallet.publicKey, txSig.trim());
        await afterCredit({
          gbuxCredited: credit.gbuxCredited,
          playGbux: credit.playGbux,
        });
        return;
      }

      const r = await fetch("/api/sales/buy-gbux", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          wallet: wallet.publicKey,
          method: tab === "usdt" ? "USDT" : "SOL",
          txSignature: txSig.trim(),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Purchase failed");
      await afterCredit(data);
    } catch (e) {
      toast({
        title: "Failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto">
        <div className="relative -mx-6 -mt-6 mb-4 h-28 overflow-hidden rounded-t-lg">
          <img
            src={BRAND.transferLoad}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
          <div className="absolute bottom-3 left-6 right-6">
            <DialogTitle className="flex items-center gap-2 text-zinc-50">
              <Coins className="h-5 w-5 text-amber-400" />
              Fund play GBUX
            </DialogTitle>
          </div>
        </div>
        <DialogHeader>
          <DialogDescription className="text-zinc-400">
            Transfer on-chain GBUX from your wallet for 1:1 play credit, or buy
            with SOL / USDT at{" "}
            <strong className="text-emerald-300">$0.001 / GBUX</strong>.
            {loading ? " · Processing…" : ""}
          </DialogDescription>
        </DialogHeader>
        {loading && (
          <div className="relative overflow-hidden rounded-lg border border-amber-500/30 h-16">
            <img
              src={BRAND.tableHero}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10 h-full flex items-center justify-center gap-2 text-sm text-amber-100">
              <Loader2 className="h-4 w-4 animate-spin" />
              Approve in Phantom…
            </div>
          </div>
        )}

        {!loggedIn && (
          <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-3 text-sm">
            <p className="mb-2">
              Optional: Grudge ID for fleet bag sync. Wallet is enough to play.
            </p>
            <Button
              className="w-full"
              onClick={() => loginWithGrudgeId("/account")}
            >
              Sign in Grudge ID
            </Button>
          </div>
        )}

        <div className="rounded-lg bg-zinc-900 p-3 text-xs space-y-1">
          <div className="flex items-center gap-1 text-emerald-400 font-semibold">
            <ShieldCheck className="h-3.5 w-3.5" />
            Signed transfers only
          </div>
          <p className="text-zinc-500">
            Mint {GBUX.mint.slice(0, 12)}… · mainnet · 5% fee on SOL/USDT buys
          </p>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList className="grid w-full grid-cols-3 bg-zinc-900">
            <TabsTrigger value="gbux">GBUX</TabsTrigger>
            <TabsTrigger value="sol">SOL</TabsTrigger>
            <TabsTrigger value="usdt">USDT</TabsTrigger>
          </TabsList>
          <TabsContent value="gbux" className="text-xs text-zinc-400 pt-2 space-y-3">
            <p>
              Send GBUX from Phantom (or other) — play credits the on-chain
              amount <strong>1:1</strong>.
            </p>
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">GBUX to transfer</label>
              <Input
                type="number"
                min={1}
                value={gbuxAmount}
                onChange={(e) => setGbuxAmount(e.target.value)}
                className="bg-zinc-900 border-zinc-700"
              />
              <Button
                type="button"
                disabled={loading || !wallet.publicKey}
                onClick={() => void onSendFromWallet()}
                className="w-full bg-emerald-700 hover:bg-emerald-600"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign &amp; send → play
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="sol" className="text-xs text-zinc-400 pt-2 space-y-3">
            <p>
              <strong className="text-emerald-300">$0.001 = 1 GBUX</strong> ·{" "}
              <strong className="text-amber-200">5% fee</strong>. Approve SOL in
              your wallet; play credits after confirm.
            </p>
            <label className="text-xs text-zinc-400">SOL to send</label>
            <Input
              type="number"
              min={0.001}
              step="any"
              value={solAmount}
              onChange={(e) => setSolAmount(e.target.value)}
              className="bg-zinc-900 border-zinc-700"
            />
            <Button
              type="button"
              disabled={loading || !wallet.publicKey}
              onClick={() => void onBuyWithSol()}
              className="w-full bg-cyan-700 hover:bg-cyan-600"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign SOL → play GBUX
            </Button>
          </TabsContent>
          <TabsContent value="usdt" className="text-xs text-zinc-400 pt-2 space-y-3">
            <p>
              USDT SPL ·{" "}
              <strong className="text-emerald-300">$0.001 = 1 GBUX</strong> ·{" "}
              <strong className="text-amber-200">5% fee</strong> (~950 GBUX per
              1 USDT).
            </p>
            <label className="text-xs text-zinc-400">USDT to send</label>
            <Input
              type="number"
              min={0.01}
              step="any"
              value={usdtAmount}
              onChange={(e) => setUsdtAmount(e.target.value)}
              className="bg-zinc-900 border-zinc-700"
            />
            <Button
              type="button"
              disabled={loading || !wallet.publicKey}
              onClick={() => void onBuyWithUsdt()}
              className="w-full bg-emerald-800 hover:bg-emerald-700"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign USDT → play GBUX
            </Button>
          </TabsContent>
        </Tabs>

        <div className="space-y-2">
          <label className="text-xs text-zinc-400">
            Already sent? Paste tx signature
          </label>
          <Input
            value={txSig}
            onChange={(e) => setTxSig(e.target.value)}
            placeholder="Solana signature"
            className="bg-zinc-900 border-zinc-700 font-mono text-xs"
          />
        </div>

        <Button
          disabled={loading || !wallet.publicKey}
          onClick={() => void onSubmit()}
          className="w-full bg-amber-600 hover:bg-amber-500"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Coins className="mr-2 h-4 w-4" />
          {tab === "gbux"
            ? "Verify deposit → play"
            : "Verify payment → play"}
        </Button>
        <p className="text-[10px] text-zinc-600 text-center">
          200 welcome on first connect · more via signed transfer ·{" "}
          {FLEET.walletSite.replace("https://", "")}
        </p>
      </DialogContent>
    </Dialog>
  );
}
